import { useCallback, useEffect, useState } from 'react';
import { assemblyService } from 'services/api';
import styles from './CuorumPanel.module.css';

/**
 * F-17 (a): cuórum de la Ley 675 sobre el TOTAL del conjunto, por coeficientes
 * (Art. 45) y por unidades (decisiones sin contenido económico en conjuntos
 * residenciales). Solo informa y deja constancia para el acta: nunca bloquea
 * — si se pierde el cuórum, el administrador y los asistentes deciden si
 * siguen o suspenden.
 *
 * compacto: solo los avisos (pantalla de Preguntas).
 */
const REFRESCO_MS = 15000;
const num  = (n) => Number(n || 0).toLocaleString('es-CO', { maximumFractionDigits: 1 });
const hora = (d) => new Date(d).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' });
const TIPO = { verificacion: 'Verificación', perdida: 'Se perdió el cuórum', recuperacion: 'Se alcanzó el cuórum' };

export default function CuorumPanel({ assembly, puedeVerificar = false, compacto = false }) {
  const [datos,       setDatos]       = useState(null);
  const [verificando, setVerificando] = useState(false);
  const [mensaje,     setMensaje]     = useState(null);
  const [verHistorial, setVerHistorial] = useState(false);
  const enCurso = assembly?.status === 'en_progreso';

  const cargar = useCallback(() => {
    if (!assembly?.id) return;
    assemblyService.cuorum(assembly.id).then(r => setDatos(r.data)).catch(() => {});
  }, [assembly?.id]);

  useEffect(() => {
    setDatos(null); setMensaje(null);
    if (!enCurso) return undefined;
    cargar();
    const t = setInterval(() => { if (document.visibilityState === 'visible') cargar(); }, REFRESCO_MS);
    return () => clearInterval(t);
  }, [cargar, enCurso]);

  const verificar = async () => {
    setVerificando(true); setMensaje(null);
    try {
      const { data } = await assemblyService.verificarCuorum(assembly.id);
      const m = data.medicion;
      setMensaje({ tipo: m.con_cuorum ? 'success' : 'warning',
        texto: m.con_cuorum
          ? `Cuórum verificado: ${num(m.pct_coef)} % de los coeficientes. La asamblea sesiona válidamente.`
          : `Sin cuórum: ${num(m.pct_coef)} % de los coeficientes (se necesita más del 50 %). Pueden esperar y volver a verificar.` });
      cargar();
    } catch (e) {
      setMensaje({ tipo: 'danger', texto: e.response?.data?.message || 'No se pudo verificar el cuórum.' });
    } finally { setVerificando(false); }
  };

  if (!enCurso || !datos) return null;
  const a = datos.actual;
  const ultima = datos.ultima_verificacion;

  const avisos = (
    <>
      {datos.perdido && (
        <div className={`alert alert-warning ${styles.aviso}`}>
          <i className="fas fa-triangle-exclamation" /> <strong>Se perdió el cuórum</strong> ({num(a.pct_coef)} % de los coeficientes presentes).
          {' '}Pueden continuar o suspender la sesión: el acta dejará constancia.
        </div>
      )}
      {!datos.verificado && (
        <div className={`alert alert-info ${styles.aviso}`}>
          <i className="fas fa-circle-info" /> Aún no se ha verificado el cuórum de esta asamblea
          {compacto ? ' (se hace en Asistentes → Verificar cuórum).' : '.'}
        </div>
      )}
    </>
  );
  if (compacto) return avisos;

  return (
    <div className={`card ${styles.tarjeta}`}>
      {avisos}
      {mensaje && <div className={`alert alert-${mensaje.tipo} ${styles.aviso}`}>{mensaje.texto}</div>}
      <div className={styles.fila}>
        <div>
          <div className={styles.titulo}>
            Cuórum del conjunto{' '}
            <span className={`badge ${a.con_cuorum ? 'badge-success' : 'badge-danger'} ${styles.etiqueta}`}>
              {a.con_cuorum ? 'Con cuórum' : 'Sin cuórum'}
            </span>
          </div>
          <div className={styles.detalle}>
            Coeficientes: <strong>{num(a.pct_coef)} %</strong> · Unidades: <strong>{a.unidades_presentes} de {a.unidades_total}</strong> ({num(a.pct_unidades)} %) · se necesita más del 50 %
          </div>
          <div className={styles.ultima}>
            {ultima
              ? <>Última verificación: {hora(ultima.created_at)} — {ultima.con_cuorum ? 'con cuórum' : 'sin cuórum'}</>
              : 'Sin verificación registrada'}
            {datos.historial?.length > 0 && (
              <button type="button" className={styles.constancias}
                onClick={() => setVerHistorial(v => !v)}>
                {verHistorial ? 'Ocultar constancias' : `Ver constancias (${datos.historial.length})`}
              </button>
            )}
          </div>
        </div>
        {puedeVerificar && (
          <button className="btn btn-primary" onClick={verificar} disabled={verificando}
            title="Guarda una constancia del cuórum de este momento para el acta">
            <i className="fas fa-clipboard-check" /> {verificando ? 'Verificando…' : 'Verificar cuórum'}
          </button>
        )}
      </div>
      {verHistorial && (
        <ul className={styles.historial}>
          {datos.historial.map(c => (
            <li key={c.id}>
              {hora(c.created_at)} — {TIPO[c.tipo]}: {num(Number(c.coef_total) > 0 ? (c.coef_presente / c.coef_total) * 100 : (c.unidades_presentes / (c.unidades_total || 1)) * 100)} % de los coeficientes,
              {' '}{c.unidades_presentes} de {c.unidades_total} unidades{c.tipo === 'verificacion' ? ` — ${c.con_cuorum ? 'con cuórum' : 'sin cuórum'}` : ''}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
